import React from 'react';
import { Divider, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
    useGetHelpArticlesByCategorySlugQuery,
    useGetHelpCategoriesQuery,
    useHelpCategories,
    useHelpCategoriesLoaded,
} from '../../api/help-categories';

import PageLayout from '../../components/page-layout';
import PageTitle from '../../components/page-title';
import CustomRating from '../../components/rating';
import { ROUTES } from '../../config/constants';
import Search from '../main/components/search';
import SearchResults from './components/searchResults';

const SearchPage = () => {

    const { t } = useTranslation()
    const { categorySlug } = useParams()
    const navigate = useNavigate()

    const [searchParams] = useSearchParams()

    const query = searchParams.get('q') || ''
    const page = searchParams.has('page') ? Number(searchParams.get('page')) : undefined

    const categories = useHelpCategories()
    const categoriesLoaded = useHelpCategoriesLoaded()
    const category = categories.find((item) => item.slug === categorySlug)

    useGetHelpCategoriesQuery(undefined, {
        skip: categoriesLoaded,
    })

    const { data: categoryArticles, isFetching } = useGetHelpArticlesByCategorySlugQuery({
        slug: categorySlug,
        search: query,
        page,
    })

    const handleCategorySearch = (value: string) => {
        if (categorySlug) {
            if (!value) {
                navigate(`/${categorySlug}`)
                return
            }

            navigate(`/${categorySlug}?q=${encodeURIComponent(value)}&page=1`)
        } else {
            navigate(`${ROUTES.SEARCH}?q=${encodeURIComponent(value)}`)
        }
    }

    const handleClearSearch = () => {
        if (categorySlug) {
            navigate(`/${categorySlug}`)
        } else {
            navigate(ROUTES.SEARCH)
        }
    }

    const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
        const params = new URLSearchParams(searchParams)

        params.set('page', `${value}`)
        navigate(`${categorySlug ? `/${categorySlug}` : ROUTES.SEARCH}?${params.toString()}`)
    }

    return (
        <PageLayout hasTitle>
            <PageTitle title={categorySlug ? category?.name || '' : t('text__searchThroughTheHelpCenter')}/>
            <Search
                initialValue={query}
                onSearch={categorySlug ? handleCategorySearch : undefined}
                onClear={handleClearSearch}
            />
            {!categorySlug && (
                <Typography variant='h5' sx={{
                    marginTop: 3,
                }}>
                    {t('text__searchResults')}
                </Typography>
            )}
            <Divider sx={{
                marginTop: 3,
            }}/>
            <SearchResults
                articles={categoryArticles?.data || []}
                categoryName={category?.name}
                isLoading={isFetching}
                page={page || 1}
                pageCount={categoryArticles?.meta.pagination.pageCount}
                onPageChange={handlePageChange}
            />
            <CustomRating title={t('text__evaluateTheSearchResults')}/>
        </PageLayout>
    )
};

export default SearchPage;
